import { AccordionAnimation } from "components/AccordionAnimation";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import ToolTip from "components/design/toolTip";
import { Info } from "components/icons/Info";
import { Padlock } from "components/icons/Padlock";
import { LockedDatePicker } from "components/LockedDatePicker/LockedDatePicker";
import { NumericInput } from "components/NumericInput/NumericInput";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { DISPLAY_DATE } from "constants/date";
import { LEDGER_TYPE_OPTIONS, LEDGER_TYPES } from "constants/ledgerEntryTypes";
import { RECURRING_JOURNAL_ENTRY } from "constants/subscriptionPermissionFeatures";
import {
  ACCOUNTING_METHOD_TYPES,
  ACCOUNTING_METHODS,
} from "constants/accountMethod";
import { Field, FieldProps, useFormikContext } from "formik";
import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import { JournalEntryTransaction } from "./AddJournalEntryModal";
import {
  getAccrualEntries,
  getCashEntries,
  getNewJournalEntryId,
} from "utils/journalEntryUtils";
import { getRecurringEntryStartDate } from "util/getRecurringEntryStartDate";
import dayjs from "dayjs";
import { FileInput, FileType } from "components/FileInput/FileInput";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { useUploadManualTransactionInvoiceMutation } from "store/apis/transactions";
import { FileData } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { PadlockIcon } from "components/icons/PadlockIcon";
import { Preview } from "components/PreviewModal";

export type InitialJournalEntryValues = {
  transactions: JournalEntryTransaction[];
  transaction_date: string;
  ledger_entry_name: string;
  ledger_type: string;
  other_ledger_type_str: string;
  is_recurring_enabled: boolean;
  frequency: string;
  end_date: string;
  max_recurring_count: number;
  ends_on: string;
  basis: ACCOUNTING_METHOD_TYPES;
  entry_level_invoice: Omit<FileData, "invoice_status" | "invoice_uuid"> | null;
};

const endsOnOptions = [
  {
    label: "On end date",
    value: "END_DATE",
  },
  {
    label: "After no. of entries",
    value: "NO_OF_ENTRIES",
  },
  {
    label: "Never ends",
    value: "NEVER_ENDS",
  },
];

const frequencyOptions = [
  {
    label: "Daily",
    value: "DAILY",
  },
  {
    label: "Weekly",
    value: "WEEKLY",
  },
  {
    label: "Monthly",
    value: "MONTHLY",
  },
  {
    label: "Quarterly",
    value: "QUARTERLY",
  },
  {
    label: "Annually",
    value: "YEARLY",
  },
];

const basisOptions = [
  {
    label: "Cash",
    value: ACCOUNTING_METHODS.CASH,
  },
  {
    label: "Accrual",
    value: ACCOUNTING_METHODS.ACCRUAL,
  },
  {
    label: "Cash & Accrual",
    value: ACCOUNTING_METHODS.CASH_AND_ACCRUAL,
  },
];

export const RecurringJournalEntrySettings = ({
  disabled,
}: {
  disabled?: boolean;
}) => {
  const { values, setValues } = useFormikContext<{
    end_date: string;
    max_recurring_count: number | null;
    transaction_date: string;
    frequency: string;
    ends_on: string;
  }>();

  let endsOnValue = values.ends_on || null;

  if (values.end_date) {
    endsOnValue ??= "END_DATE";
  }

  if (values.max_recurring_count && values.max_recurring_count >= 0) {
    endsOnValue ??= "NO_OF_ENTRIES";
  }

  if (!values.end_date && !values.max_recurring_count) {
    endsOnValue ??= "NEVER_ENDS";
  }

  const onEndOptionChange = (option?: string) => {
    if (option === "NEVER_ENDS") {
      return setValues({
        ...values,
        end_date: "",
        max_recurring_count: null,
        ends_on: option,
      });
    }

    if (option === "END_DATE") {
      return setValues({
        ...values,
        max_recurring_count: null,
        ends_on: option,
      });
    }

    if (option === "NO_OF_ENTRIES") {
      return setValues({
        ...values,
        end_date: "",
        ends_on: option,
      });
    }
  };

  return (
    <>
      <DateField
        name="start_on"
        dateProps={{
          label: "Starts on",
          portalId: "start_on",
          required: true,
          maxDate: new Date(),
          placeholder: DISPLAY_DATE,
          disabled: true,
          block: true,
        }}
      />

      <Combobox
        required
        menuPortalTarget={document.body}
        label="Frequency"
        withForm
        block
        options={frequencyOptions}
        value={frequencyOptions.find((v) => v.value === values.frequency)}
        name="frequency"
        isDisabled={disabled}
      />

      <div className="t-w-full">
        <Combobox
          required
          menuPortalTarget={document.body}
          label="Ends"
          block
          isClearable={false}
          onChange={(option) => {
            if (option instanceof Array) {
              return;
            }

            onEndOptionChange(option?.value);
          }}
          value={endsOnOptions.find((v) => v.value === endsOnValue)}
          options={endsOnOptions}
          name="ends_on"
          isDisabled={disabled}
        />
      </div>

      {endsOnValue === "END_DATE" && (
        <Field name="end_date">
          {({ field }: FieldProps) => {
            return (
              <DateInput
                required
                label="End date"
                {...field}
                portalId={field.name}
                showErrorOnceTouched={false}
                minDate={new Date()}
                placeholder={DISPLAY_DATE}
                block
                disabled={disabled}
              />
            );
          }}
        </Field>
      )}

      {endsOnValue === "NO_OF_ENTRIES" && (
        <NumericInput
          required
          numericProps={{
            disabled: disabled,
          }}
          label="Number of entries"
          fieldProps={{
            name: "max_recurring_count",
          }}
        />
      )}
    </>
  );
};

const JournalEntryAttachment = () => {
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const [uploadManualTransactionInvoice, { isLoading: isUploading }] =
    useUploadManualTransactionInvoiceMutation();

  const { setFieldValue, values } = useFormikContext<{
    entry_level_invoice: InitialJournalEntryValues["entry_level_invoice"];
    transactions: InitialJournalEntryValues["transactions"];
  }>();

  const { entry_level_invoice, transactions } = values;

  const onDrop = async (files: FileType[]) => {
    try {
      const file = await uploadManualTransactionInvoice({
        invoice: files[0] as File,
        entityId,
        groupId,
      }).unwrap();
      setFieldValue("entry_level_invoice", file);
      transactions.forEach((_, index) => {
        setFieldValue(`transactions[${index}].invoice`, file);
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onDelete = () => {
    setFieldValue("entry_level_invoice", null);
    transactions.forEach((_, index) => {
      setFieldValue(`transactions[${index}].invoice`, null);
    });
  };

  const attachmentLabel = (
    <span className="t-flex t-items-center t-gap-1">
      Attachment
      <ToolTip
        text="Adding a file here applies it to all line items. Files uploaded on individual line items are retained when no file is added here."
        side="right"
      >
        <span className="t-flex t-items-center t-text-text-30">
          <Info />
        </span>
      </ToolTip>
    </span>
  );

  return (
    <div className="t-w-1/2 sm:t-w-full">
      <FileInput
        label={attachmentLabel}
        block
        file={
          entry_level_invoice
            ? {
                uuid: entry_level_invoice.uuid,
                name: entry_level_invoice.name,
                file_type: entry_level_invoice.file_type,
                is_previewable: entry_level_invoice.is_previewable,
              }
            : null
        }
        onDrop={onDrop}
        isUploading={isUploading}
        onDelete={onDelete}
        onFileClick={({ uuid: fileId }) => setPreviewFileId(fileId)}
      />
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={() => setPreviewFileId(null)}
        groupId={groupId}
        previewId={previewFileId}
      />
    </div>
  );
};

export const JournalEntryInfo = () => {
  const { values, setFieldValue } =
    useFormikContext<InitialJournalEntryValues>();

  const selectedLedgerType = values.ledger_type
    ? LEDGER_TYPE_OPTIONS.find((option) => option.value === values.ledger_type)
    : null;

  const changeTransactionsToAccrual = (
    transactions: JournalEntryTransaction[]
  ) => {
    setFieldValue(
      "transactions",
      transactions.map((t) => ({
        ...t,
        accrual_category_id: t.cash_category_id || t.accrual_category_id,
        cash_category_id: "",
        id: getNewJournalEntryId(),
        uuid: getNewJournalEntryId(),
        transaction_id: undefined,
        entry_type: ACCOUNTING_METHODS.ACCRUAL,
      }))
    );
  };

  const changeTransactionsToCash = (
    transactions: JournalEntryTransaction[]
  ) => {
    setFieldValue(
      "transactions",
      transactions.map((t) => ({
        ...t,
        cash_category_id: t.cash_category_id || t.accrual_category_id,
        accrual_category_id: "",
        id: getNewJournalEntryId(),
        uuid: getNewJournalEntryId(),
        transaction_id: undefined,
        entry_type: ACCOUNTING_METHODS.CASH,
      }))
    );
  };

  const onLedgerEntryChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (option instanceof Array) {
      return null;
    }

    if (option?.value === LEDGER_TYPES.PAYMENT_PROCESSOR) {
      setFieldValue("basis", ACCOUNTING_METHODS.CASH_AND_ACCRUAL);
      return setFieldValue("is_cash_same_as_accrual", true);
    }

    if (option?.value !== LEDGER_TYPES.OTHERS) {
      const accrualTransactions = getAccrualEntries(values.transactions);
      changeTransactionsToAccrual(
        accrualTransactions.length ? accrualTransactions : values.transactions
      );
      setFieldValue("basis", ACCOUNTING_METHODS.ACCRUAL);
    }

    return setFieldValue("is_cash_same_as_accrual", false);
  };

  const onBasisChange = (
    option: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (option instanceof Array) {
      return null;
    }

    if (option?.value === ACCOUNTING_METHODS.CASH_AND_ACCRUAL) {
      changeTransactionsToAccrual(values.transactions);
      return setFieldValue("is_cash_same_as_accrual", true);
    }

    const cashTransactions = getCashEntries(values.transactions);
    const accrualTransactions = getAccrualEntries(values.transactions);

    if (option?.value === ACCOUNTING_METHODS.CASH) {
      changeTransactionsToCash(
        cashTransactions.length ? cashTransactions : accrualTransactions
      );
    }

    if (option?.value === ACCOUNTING_METHODS.ACCRUAL) {
      console.log(
        accrualTransactions.length ? accrualTransactions : cashTransactions
      );
      changeTransactionsToAccrual(
        accrualTransactions.length ? accrualTransactions : cashTransactions
      );
    }

    return setFieldValue("is_cash_same_as_accrual", false);
  };

  const onTransactionDateChange = (date: Date) => {
    setFieldValue(
      "start_on",
      getRecurringEntryStartDate(dayjs(date).format(DISPLAY_DATE))
    );
  };

  return (
    <>
      <div className="t-flex t-gap-6 t-items-start sm:t-flex-col sm:t-items-stretch sm:t-gap-3">
        <Field name="transaction_date">
          {({ field }: FieldProps) => {
            const isValidDate = dayjs(field.value).isValid();
            return (
              <LockedDatePicker
                label="Date"
                {...field}
                value={isValidDate ? field.value : null}
                name="transaction_date"
                portalId="journal_entry_date"
                required
                block
                customSize="small"
                placeholder={DISPLAY_DATE}
                onDateChange={onTransactionDateChange}
              />
            );
          }}
        </Field>
        <div className="t-mt-0.5 t-w-full">
          <TextInput
            name="ledger_entry_name"
            label="Journal entry title"
            required
            placeholder="#1"
            customSize="small"
          />
        </div>
        <Combobox
          name="ledger_type"
          required
          label="Type"
          withForm
          block
          size="small"
          options={LEDGER_TYPE_OPTIONS}
          placeholder="Select type"
          menuPortalTarget={document.body}
          value={selectedLedgerType}
          onChange={onLedgerEntryChange}
        />
        {values.ledger_type === LEDGER_TYPES.OTHERS && (
          <TextInput
            name="other_ledger_type_str"
            label="Specify Others"
            required
            placeholder="Enter other type"
            block
            customSize="small"
          />
        )}

        <Combobox
          name="basis"
          required
          label="Basis"
          withForm
          block
          size="small"
          options={basisOptions}
          placeholder="Select basis"
          menuPortalTarget={document.body}
          value={basisOptions.find((option) => option.value === values.basis)}
          onChange={onBasisChange}
          isDisabled={values.ledger_type !== LEDGER_TYPES.OTHERS}
        />
      </div>

      <div className="t-flex t-gap-2">
        <PermissionBasedUI
          blockedUI={
            <Checkbox
              disabled={!values.is_recurring_enabled}
              name="is_recurring_enabled"
              label={
                <span className="t-flex t-gap-1 t-justify-center">
                  <span>This is a recurring entry</span>
                  <span>
                    <PadlockIcon />
                  </span>
                </span>
              }
              checked={values.is_recurring_enabled}
              onChange={(e) =>
                setFieldValue("is_recurring_enabled", e.target.checked)
              }
            />
          }
          feature={RECURRING_JOURNAL_ENTRY}
        >
          <Checkbox
            name="is_recurring_enabled"
            label="This is a recurring entry"
            checked={values.is_recurring_enabled}
            onChange={(e) =>
              setFieldValue("is_recurring_enabled", e.target.checked)
            }
          />
        </PermissionBasedUI>
      </div>

      <AnimatePresence>
        {values.is_recurring_enabled && (
          <AccordionAnimation>
            <div className="t-flex t-gap-6 sm:t-flex-col sm:t-gap-3">
              <RecurringJournalEntrySettings />
            </div>
          </AccordionAnimation>
        )}
      </AnimatePresence>

      <JournalEntryAttachment />
    </>
  );
};
